import {
  canEditInvoicePoAllocations,
  hasReconciledPoAllocation,
  shouldOfferAllocateMultiPo,
} from './invoiceMultiDocRules'

/**
 * Multi-PO allocation helpers (dashboard side).
 * Mirrors express validation (utils/invoicePoAllocation.js): every row needs
 * a PO and a positive amount, POs must be unique, and the allocated sum must
 * equal the invoice total exactly (whole pennies).
 *
 * Step 9: chefs may edit saved slices until any allocated PO is reconciled
 * or the invoice is sent to Xero (same spirit as Remove from this PO).
 */

export const MIN_ALLOCATION_ROWS = 2

export const EDIT_ALLOCATIONS_KIND = {
  NONE: 'none',
  BLOCKED: 'blocked',
  ALLOCATE: 'allocate',
  EDIT: 'edit',
}

export const ALLOCATE_BUTTON_LABEL = 'Allocate'
export const EDIT_ALLOCATIONS_BUTTON_LABEL = 'Edit allocations'

export const EDIT_ALLOCATIONS_BLOCKED_MESSAGE =
  'Allocations cannot be edited after a purchase order slice is reconciled or the invoice is closed.'

let allocationRowKeyCounter = 0

export function createAllocationRow(overrides = {}) {
  allocationRowKeyCounter += 1
  return {
    key: `allocation-row-${allocationRowKeyCounter}`,
    purchaseOrderNumber: '',
    amountPounds: '',
    orderId: null,
    ...overrides,
  }
}

export function allocationPoundsToPennies(pounds) {
  if (pounds == null || pounds === '') {
    return null
  }
  const value = Number(pounds)
  if (!Number.isFinite(value)) {
    return null
  }
  return Math.round(value * 100)
}

export function allocationPenniesToPoundsInput(pennies) {
  if (pennies == null || pennies === '') {
    return ''
  }
  const value = Number(pennies)
  if (!Number.isFinite(value)) {
    return ''
  }
  return (Math.round(value) / 100).toFixed(2)
}

export function formatAllocationPennies(pennies) {
  if (pennies == null || !Number.isFinite(Number(pennies))) {
    return '—'
  }
  const pounds = Math.round(Number(pennies)) / 100
  return pounds < 0
    ? `-£${Math.abs(pounds).toFixed(2)}`
    : `£${pounds.toFixed(2)}`
}

function hasSavedMultiPoAllocations(invoice = {}) {
  return (
    Array.isArray(invoice.poAllocations) && invoice.poAllocations.length >= 2
  )
}

/**
 * True when a saved multi-PO split already adds up to the invoice total.
 * Reconcile should reload those amounts and not ask the chef to allocate again.
 */
export function listOpenAllocationPurchaseOrders(allocations = []) {
  return (Array.isArray(allocations) ? allocations : [])
    .filter(row => row && !row.reconciledAt)
    .map(row => String(row.purchaseOrderNumber || '').trim())
    .filter(Boolean)
}

function joinPurchaseOrderNames(numbers) {
  if (numbers.length <= 1) return numbers[0] || ''
  if (numbers.length === 2) return `${numbers[0]} and ${numbers[1]}`
  return `${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`
}

/**
 * After a split is saved, name the slices that are not reconciled yet and
 * say what Match & reconcile will do with them.
 */
export function describeSavedSplitNextStep(allocations = []) {
  const open = listOpenAllocationPurchaseOrders(allocations)
  if (!open.length) {
    return 'Every allocated purchase order is already reconciled.'
  }
  const named = joinPurchaseOrderNames(open)
  const verb = open.length === 1 ? 'is' : 'are'
  return `${named} ${verb} still open. Match & reconcile will close each open purchase order whose goods receipt already matches its share, and will name any that stay open.`
}

export function savedAllocationsMatchInvoiceTotal(invoice = {}) {
  const rows = Array.isArray(invoice?.poAllocations)
    ? invoice.poAllocations
    : []
  if (rows.length < 2) return false
  const total = Math.round(Number(invoice.invoiceTotal))
  if (!Number.isFinite(total)) return false
  const sum = rows.reduce((acc, row) => {
    const pennies = Math.round(Number(row?.amountPennies))
    return acc + (Number.isFinite(pennies) ? pennies : 0)
  }, 0)
  return sum === total
}

/**
 * Human copy when edit is blocked (Remove-from-PO spirit).
 */
export function getEditAllocationsBlockReason(invoice = {}) {
  if (invoice?.isArchived) {
    return 'Archived invoices cannot have their allocations edited.'
  }
  const lifecycle = String(invoice?.status || 'pending').trim() || 'pending'
  if (lifecycle === 'sentToXero') {
    return 'This invoice has been sent to Xero and its allocations cannot be edited here.'
  }
  if (lifecycle === 'reconciled') {
    return 'This invoice is reconciled and its allocations cannot be edited here.'
  }
  if (hasReconciledPoAllocation(invoice)) {
    return 'A purchase order slice is already reconciled. Allocations cannot be edited — same protection as Remove from this PO.'
  }
  if (!canEditInvoicePoAllocations(invoice)) {
    return EDIT_ALLOCATIONS_BLOCKED_MESSAGE
  }
  return ''
}

/**
 * Shared view-model for the Invoices Allocate / Edit allocations CTA.
 *
 * @returns {{
 *   kind: 'none'|'blocked'|'allocate'|'edit',
 *   canShow: boolean,
 *   canEdit: boolean,
 *   buttonLabel: string,
 *   tooltip: string,
 *   blockReason: string,
 *   isEdit: boolean,
 * }}
 */
export function getEditAllocationsActionMeta(invoice = {}) {
  const isEdit = hasSavedMultiPoAllocations(invoice)
  const buttonLabel = isEdit
    ? EDIT_ALLOCATIONS_BUTTON_LABEL
    : ALLOCATE_BUTTON_LABEL
  const canEdit = canEditInvoicePoAllocations(invoice)
  const canShow = shouldOfferAllocateMultiPo(invoice)

  if (invoice?.isArchived) {
    return {
      kind: EDIT_ALLOCATIONS_KIND.NONE,
      canShow: false,
      canEdit: false,
      buttonLabel,
      tooltip: '',
      blockReason: getEditAllocationsBlockReason(invoice),
      isEdit,
    }
  }

  if (!canEdit) {
    return {
      kind: EDIT_ALLOCATIONS_KIND.BLOCKED,
      canShow: false,
      canEdit: false,
      buttonLabel,
      tooltip: '',
      blockReason: getEditAllocationsBlockReason(invoice),
      isEdit,
    }
  }

  if (!canShow) {
    return {
      kind: EDIT_ALLOCATIONS_KIND.NONE,
      canShow: false,
      canEdit: true,
      buttonLabel,
      tooltip: '',
      blockReason: '',
      isEdit,
    }
  }

  return {
    kind: isEdit ? EDIT_ALLOCATIONS_KIND.EDIT : EDIT_ALLOCATIONS_KIND.ALLOCATE,
    canShow: true,
    canEdit: true,
    buttonLabel,
    tooltip: isEdit
      ? 'Fix how this invoice is split across purchase orders (before any PO is reconciled)'
      : 'Split this invoice across multiple purchase orders',
    blockReason: '',
    isEdit,
  }
}

/**
 * Show Allocate only for true multi-PO coverage:
 * covers_multiple_orders, saved splits, or engine multi-PO status.
 * Plain multiple_matches → Match / pick one PO (not Allocate).
 * Hidden once any allocated slice is reconciled (Step 9).
 */
export function canShowAllocateAction(invoice = {}) {
  return getEditAllocationsActionMeta(invoice).canShow
}

/**
 * Rows for the allocate modal. Reuses saved allocations when present,
 * otherwise seeds from the suggested/linked PO plus an empty row.
 * Optional `seedSelections` (from Match “add another order”) wins over saved
 * slices so the user’s current list is what they allocate.
 * Always returns at least MIN_ALLOCATION_ROWS rows.
 */
export function buildAllocationDraftRows(invoice = {}, seedSelections) {
  const seeds = Array.isArray(seedSelections)
    ? seedSelections.filter(row =>
        String(row?.purchaseOrderNumber || '').trim(),
      )
    : []

  if (seeds.length) {
    const savedByPo = new Map(
      (Array.isArray(invoice.poAllocations) ? invoice.poAllocations : [])
        .filter(row => String(row?.purchaseOrderNumber || '').trim())
        .map(row => [
          String(row.purchaseOrderNumber).trim().toUpperCase(),
          row,
        ]),
    )
    const seeded = seeds.map(row => {
      const purchaseOrderNumber = String(row.purchaseOrderNumber || '').trim()
      const saved = savedByPo.get(purchaseOrderNumber.toUpperCase())
      return createAllocationRow({
        purchaseOrderNumber,
        amountPounds: saved
          ? allocationPenniesToPoundsInput(saved.amountPennies)
          : row.amountPounds == null || row.amountPounds === ''
          ? ''
          : String(row.amountPounds),
        orderId: row.linkedOrderId
          ? String(row.linkedOrderId)
          : row.orderId
          ? String(row.orderId)
          : saved?.orderId
          ? String(saved.orderId)
          : null,
      })
    })
    while (seeded.length < MIN_ALLOCATION_ROWS) {
      seeded.push(createAllocationRow())
    }
    return seeded
  }

  const saved = Array.isArray(invoice.poAllocations)
    ? invoice.poAllocations.filter(row => row && typeof row === 'object')
    : []

  const rows = saved.map(row =>
    createAllocationRow({
      purchaseOrderNumber: String(row.purchaseOrderNumber || '').trim(),
      amountPounds: allocationPenniesToPoundsInput(row.amountPennies),
      orderId: row.orderId ? String(row.orderId) : null,
    }),
  )

  if (!rows.length) {
    const suggestedPo = String(
      invoice.suggestedPurchaseOrderNumber || invoice.purchaseOrderNumber || '',
    ).trim()
    if (suggestedPo) {
      rows.push(
        createAllocationRow({
          purchaseOrderNumber: suggestedPo,
          orderId: invoice.suggestedLinkedOrderId
            ? String(invoice.suggestedLinkedOrderId)
            : invoice.linkedOrderId
            ? String(invoice.linkedOrderId)
            : null,
        }),
      )
    }
  }

  while (rows.length < MIN_ALLOCATION_ROWS) {
    rows.push(createAllocationRow())
  }

  return rows
}

function isRowEmpty(row = {}) {
  return (
    !String(row.purchaseOrderNumber || '').trim() &&
    (row.amountPounds == null || row.amountPounds === '')
  )
}

/**
 * Rows with both a PO and a parseable amount (may still fail validation
 * on positivity/duplicates/sum).
 */
export function getCompletedAllocationRows(rows = []) {
  return (rows || []).filter(
    row =>
      row &&
      String(row.purchaseOrderNumber || '').trim() &&
      allocationPoundsToPennies(row.amountPounds) != null,
  )
}

/** Running totals for the modal footer. */
export function summariseAllocationDraft(
  rows = [],
  invoiceTotalPennies = null,
) {
  const allocatedPennies = getCompletedAllocationRows(rows).reduce(
    (sum, row) => sum + allocationPoundsToPennies(row.amountPounds),
    0,
  )

  const invoiceTotal =
    invoiceTotalPennies == null || !Number.isFinite(Number(invoiceTotalPennies))
      ? null
      : Math.round(Number(invoiceTotalPennies))

  return {
    allocatedPennies,
    invoiceTotalPennies: invoiceTotal,
    remainingPennies:
      invoiceTotal == null ? null : invoiceTotal - allocatedPennies,
  }
}

/**
 * Client-side validation mirroring the express rules so users get instant
 * feedback before the request. Returns { valid, errors: [string] }.
 */
export function validateAllocationDraft(rows = [], invoiceTotalPennies = null) {
  const errors = []

  const invoiceTotal =
    invoiceTotalPennies == null || !Number.isFinite(Number(invoiceTotalPennies))
      ? null
      : Math.round(Number(invoiceTotalPennies))

  if (invoiceTotal == null) {
    errors.push(
      'This invoice has no total yet. Add the invoice total before allocating across purchase orders.',
    )
  }

  const activeRows = (rows || []).filter(row => row && !isRowEmpty(row))
  const seenPos = new Set()
  const completed = []

  activeRows.forEach((row, index) => {
    const po = String(row.purchaseOrderNumber || '').trim()
    if (!po) {
      errors.push(`Row ${index + 1}: choose a purchase order.`)
      return
    }

    const poKey = po.toUpperCase()
    if (seenPos.has(poKey)) {
      errors.push(`Purchase order ${po} appears more than once.`)
      return
    }
    seenPos.add(poKey)

    const amountPennies = allocationPoundsToPennies(row.amountPounds)
    if (amountPennies == null || amountPennies <= 0) {
      errors.push(
        `Row ${index + 1} (${po}): enter an amount greater than £0.00.`,
      )
      return
    }

    completed.push({ purchaseOrderNumber: po, amountPennies })
  })

  if (completed.length < MIN_ALLOCATION_ROWS) {
    errors.push(
      'Allocate across at least two purchase orders (use Link PO instead for a single order).',
    )
  }

  if (invoiceTotal != null && completed.length >= MIN_ALLOCATION_ROWS) {
    const allocated = completed.reduce((sum, row) => sum + row.amountPennies, 0)
    if (allocated !== invoiceTotal) {
      const difference = invoiceTotal - allocated
      errors.push(
        difference > 0
          ? `Allocated ${formatAllocationPennies(
              allocated,
            )} of ${formatAllocationPennies(
              invoiceTotal,
            )} — ${formatAllocationPennies(difference)} still unallocated.`
          : `Allocated ${formatAllocationPennies(
              allocated,
            )} is ${formatAllocationPennies(
              Math.abs(difference),
            )} over the invoice total of ${formatAllocationPennies(
              invoiceTotal,
            )}.`,
      )
    }
  }

  return { valid: errors.length === 0, errors }
}

/** Request body for POST invoices/:id/allocations. */
export function buildAllocationsRequestBody(rows = []) {
  return {
    allocations: getCompletedAllocationRows(rows).map(row => {
      const allocation = {
        purchaseOrderNumber: String(row.purchaseOrderNumber).trim(),
        amountPennies: allocationPoundsToPennies(row.amountPounds),
      }
      if (row.orderId) {
        allocation.orderId = String(row.orderId)
      }
      return allocation
    }),
  }
}
